import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaEdit } from 'react-icons/fa';
import AccountSessionError from './AccountSessionError';
import {
  apiGetProfile,
  clearStoredAuth,
  clearStoredAuthIfUnauthorized,
  profileFetchErrorMessage,
  profilePayloadToForm,
  readStoredAuth,
  SIGN_IN_REQUIRED_MESSAGE,
} from './profileFormUtils';
import { stripPhoneDigits, formatPhoneForDisplay } from '../../utils/phone';
import { formatDisplayDate } from '../../utils/dateFormat';
import AccountIdentityChangeModal from './AccountIdentityChangeModal';
import './AccountLayout.css';

const emptyForm = {
  first_name: '',
  last_name: '',
  gender: '',
  country: 'India',
  state: '',
  city: '',
  pincode: '',
  date_of_birth: '',
  guardian_name: '',
  guardian_email: '',
  guardian_mobile: '',
};

function ProfileRow({ label, value, onChange }) {
  const display = value != null && String(value).trim() !== '' ? String(value).trim() : '—';
  return (
    <div className="account-profile-view__row">
      <dt>{label}</dt>
      <dd>
        {display}
        {onChange ? (
          <button type="button" className="account-profile-view__change-btn" onClick={onChange}>
            Change
          </button>
        ) : null}
      </dd>
    </div>
  );
}

const AccountDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [fetching, setFetching] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [savedMessage, setSavedMessage] = useState('');
  const [account, setAccount] = useState({
    id: '',
    username: '',
    email: '',
    phone_number: '',
  });
  const [form, setForm] = useState(() => ({ ...emptyForm }));
  const [identityChangeType, setIdentityChangeType] = useState(null);

  useEffect(() => {
    const msg =
      typeof location.state?.profileSavedMessage === 'string'
        ? location.state.profileSavedMessage.trim()
        : '';
    if (!msg) return;
    setSavedMessage(msg);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const mergeStoredIdentity = useCallback((normalized) => {
    const stored = readStoredAuth();
    const u = stored.user || {};
    const next = {
      ...normalized,
      form: { ...emptyForm, ...(normalized.form || {}) },
    };
    if ((next.id === undefined || next.id === null || next.id === '') && u.id != null) {
      next.id = u.id;
    }
    if (!next.email && u.email) next.email = u.email;
    if (!next.username && u.username) next.username = String(u.username).trim();
    if (!next.phone_number && u.mobile) {
      next.phone_number = stripPhoneDigits(u.mobile);
    }
    if (!next.form.first_name && u.name) {
      const parts = String(u.name).trim().split(/\s+/).filter(Boolean);
      if (parts[0]) next.form.first_name = parts[0];
      if (parts.length > 1) next.form.last_name = parts.slice(1).join(' ');
    }
    return next;
  }, []);

  const loadProfile = useCallback(async () => {
    setLoadError('');
    setFetching(true);
    const stored = readStoredAuth();
    const token = stored.token;
    if (!token) {
      clearStoredAuth();
      setLoadError(SIGN_IN_REQUIRED_MESSAGE);
      setFetching(false);
      return;
    }
    try {
      const { res, data } = await apiGetProfile(token);
      if (!res.ok) {
        clearStoredAuthIfUnauthorized(res.status);
        throw new Error(profileFetchErrorMessage(res.status, data));
      }
      let normalized = profilePayloadToForm(data);
      normalized = mergeStoredIdentity(normalized);
      setAccount({
        id: normalized.id ?? '',
        username: normalized.username || '',
        email: normalized.email || '',
        phone_number: normalized.phone_number || '',
      });
      setForm({ ...emptyForm, ...normalized.form });
    } catch (e) {
      setLoadError(e.message || 'Failed to load profile.');
      const fallback = mergeStoredIdentity(profilePayloadToForm({}));
      setAccount({
        id: fallback.id ?? '',
        username: fallback.username || '',
        email: fallback.email || '',
        phone_number: fallback.phone_number || '',
      });
      setForm({ ...emptyForm, ...fallback.form });
    } finally {
      setFetching(false);
    }
  }, [mergeStoredIdentity]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  return (
    <div className="account-page">
      <div className="account-page__title-block">
        <h1>My Profile</h1>
        <p className="account-page__muted">Your DigiRasa account details.</p>
      </div>

      {loadError ? (
        <AccountSessionError
          message={loadError}
          returnPath="/account"
          signInSuffix="to view your profile"
          onRetry={loadProfile}
          variant="inline"
        />
      ) : null}

      {savedMessage ? (
        <div
          className="auth-inline-message auth-inline-message--success"
          role="status"
          aria-live="polite"
        >
          {savedMessage}
        </div>
      ) : null}

      {fetching ? (
        <p className="account-page__muted">Loading profile…</p>
      ) : (
        <section className="account-profile-view" aria-label="Profile details">
          <h2 className="account-profile-view__section-title">Account</h2>
          <dl className="account-profile-view__list">
            <ProfileRow label="Username" value={account.username} />
            <ProfileRow label="Email" value={account.email} onChange={() => setIdentityChangeType('email')} />
            <ProfileRow
              label="Mobile"
              value={formatPhoneForDisplay(account.phone_number)}
              onChange={() => setIdentityChangeType('phone')}
            />
          </dl>

          <div className="account-profile-view__section-head">
            <h2 className="account-profile-view__section-title">Profile</h2>
            <Link
              to="/account/details"
              className="account-profile-view__edit-icon"
              aria-label="Edit profile details"
              title="Edit profile details"
            >
              <FaEdit aria-hidden />
            </Link>
          </div>
          <dl className="account-profile-view__list">
            <ProfileRow label="First name" value={form.first_name} />
            <ProfileRow label="Last name" value={form.last_name} />
            <ProfileRow label="Gender" value={form.gender} />
            <ProfileRow label="Date of birth" value={formatDisplayDate(form.date_of_birth, '—')} />
            <ProfileRow label="Country" value={form.country} />
            <ProfileRow label="State" value={form.state} />
            <ProfileRow label="City" value={form.city} />
            <ProfileRow label="Pin code" value={form.pincode} />
          </dl>

          <h2 className="account-profile-view__section-title">Guardian</h2>
          <dl className="account-profile-view__list">
            <ProfileRow label="Guardian name" value={form.guardian_name} />
            <ProfileRow label="Guardian email" value={form.guardian_email} />
            <ProfileRow label="Guardian mobile" value={formatPhoneForDisplay(form.guardian_mobile)} />
          </dl>
        </section>
      )}
      {identityChangeType ? (
        <AccountIdentityChangeModal
          changeType={identityChangeType}
          currentEmail={account.email}
          currentPhone={account.phone_number}
          onClose={() => setIdentityChangeType(null)}
          onCompleted={(data) => {
            setAccount((current) => ({
              ...current,
              email: data.email || current.email,
              phone_number: data.phone_number || current.phone_number,
            }));
          }}
        />
      ) : null}
    </div>
  );
};

export default AccountDashboard;
